import React from 'react';
import type { Moment } from 'moment';
import styled, { css } from 'styled-components';
import Icon from '../../icon/Icon';
import { GetColor } from '../../../style';
import { noop, partial } from 'lodash';
import { cva, type VariantProps } from 'class-variance-authority';
import { Match } from 'effect';

type RootProps = {
  className: string | undefined;
  children: React.ReactNode;
};

type CellProps = {
  date: Moment;
  visibleMonth: Moment;
  valueDate: Moment;
  handleSelect: (date: Moment) => void;
  isDisabled: (date: Moment) => boolean;
  children: React.ReactNode;
  variant: VariantProps<typeof cellVariants>;
};

const Row = styled.div`
  display: flex;
  font-size: 1.125rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 30px;
  margin-bottom: 5px;
`;

type PreviousMonthButtonProps = {
  handlePreviousMonth: () => void;
  variant: VariantProps<typeof monthButtonVariants>;
  disabled: boolean;
};
type CurrentMonthProps = {
  visibleMonth: Moment;
  variant: VariantProps<typeof currentMonthVariants>;
};
type NextMonthButtonProps = {
  handleNextMonth: () => void;
  variant: VariantProps<typeof monthButtonVariants>;
  disabled: boolean;
};
type DayRowProps = {
  variant: VariantProps<typeof dayCellVariants>;
};

const cellVariants = cva('', {
  variants: {
    color: {
      default: '',
      dark: '[&&]:border-venn-grey-30 text-venn-black',
    },
    type: {
      value: '',
      empty: 'bg-venn-grey-10',
    },
  },
});

const monthButtonVariants = cva('', {
  variants: {
    color: {
      default: 'text-venn-primary-dark',
      dark: 'text-venn-dark-blue',
      grey: 'text-venn-grey-30',
    },
  },
});

const currentMonthVariants = cva('text-[12px]', {
  variants: {
    color: {
      default: 'text-venn-grey-60 font-bold',
      dark: 'text-venn-black font-semibold',
    },
  },
});

const dayCellVariants = cva('w-[30px] text-center', {
  variants: {
    size: {
      small: '',
      large: 'h-[30px] content-center justify-center text-[10px]',
    },
    color: {
      light: 'text-venn-grey-50',
      dark: 'text-venn-grey-80',
    },
  },
});

export const CalendarPrimitives = {
  Header,
  PreviousMonthButton: ({ handlePreviousMonth, variant, disabled }: PreviousMonthButtonProps) => (
    <NavIcon
      type="chevron-circle-left"
      prefix={Match.value(variant.color).pipe(
        Match.when('dark', () => 'fas' as const),
        Match.when('grey', () => 'fas' as const),
        Match.when('default', () => 'fal' as const),
        Match.orElse(() => 'fal' as const),
      )}
      onClick={handlePreviousMonth}
      tabIndex={-1}
      className={monthButtonVariants(variant)}
      disabled={disabled}
      iconLabel="Previous Month"
    />
  ),
  NextMonthButton: ({ handleNextMonth, variant, disabled }: NextMonthButtonProps) => (
    <NavIcon
      type="chevron-circle-right"
      prefix={Match.value(variant.color).pipe(
        Match.when('dark', () => 'fas' as const),
        Match.when('grey', () => 'fas' as const),
        Match.when('default', () => 'fal' as const),
        Match.orElse(() => 'fal' as const),
      )}
      onClick={handleNextMonth}
      tabIndex={-1}
      className={monthButtonVariants(variant)}
      disabled={disabled}
      iconLabel="Next Month"
    />
  ),
  CurrentMonth: ({ visibleMonth, variant }: CurrentMonthProps) => (
    <div className={currentMonthVariants(variant)}>{visibleMonth.format('MMMM YYYY')}</div>
  ),
  DayRow: ({ variant }: DayRowProps) => {
    return (
      <CalendarPrimitives.Row>
        <div className={dayCellVariants(variant)}>Su</div>
        <div className={dayCellVariants(variant)}>Mo</div>
        <div className={dayCellVariants(variant)}>Tu</div>
        <div className={dayCellVariants(variant)}>We</div>
        <div className={dayCellVariants(variant)}>Th</div>
        <div className={dayCellVariants(variant)}>Fr</div>
        <div className={dayCellVariants(variant)}>Sa</div>
      </CalendarPrimitives.Row>
    );
  },
  Root: ({ className, children }: RootProps) => {
    return (
      <div>
        <Container className={className}>{children}</Container>
      </div>
    );
  },
  Cell: ({
    color,
    date,
    visibleMonth,
    valueDate,
    handleSelect,
    isDisabled,
    children,
  }: CellProps & VariantProps<typeof cellVariants>) => {
    const disabled = isDisabled(date);
    return (
      <InnerCell
        role="option"
        disabled={disabled}
        otherMonth={date.month() !== visibleMonth.month()}
        selected={date.isSame(valueDate, 'day')}
        onClick={disabled ? noop : partial(handleSelect, date)}
        key={date.valueOf()}
        className={cellVariants({ color })}
      >
        {children}
      </InnerCell>
    );
  },
  EmptyCell: () => (
    <EmptyCell className={cellVariants({ color: 'dark', type: 'empty' })} onClick={noop} tabIndex={-1} />
  ),
  Row,
};

const NavIcon = styled(Icon)`
  font-size: 12px;
  margin: 0 8px;
`;

const Container = styled.div`
  border: 1px solid ${GetColor.GreyScale.Grey60};
  display: inline-block;
`;

const EmptyCell = styled.div`
  width: 30px;
  height: 30px;
  border-left: 1px solid ${GetColor.GreyScale.Grey20};
  border-top: 1px solid ${GetColor.GreyScale.Grey20};
  text-align: center;
  vertical-align: middle;
  line-height: 30px;
  cursor: pointer;

  &:first-child {
    border-left: none;
  }
`;

const InnerCell = styled(EmptyCell)<{ selected: boolean; otherMonth: boolean; disabled: boolean }>`
  color: ${(props) => (props.disabled || props.otherMonth ? GetColor.GreyScale.Grey50 : GetColor.Black)};

  ${(props) =>
    props.selected
      ? css`
          color: ${GetColor.White};
          background-color: ${GetColor.Primary.Dark};
        `
      : ''}
  &:hover {
    background-color: ${GetColor.Primary.Medium};
  }
`;
